import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { adminMiddleware, authMiddleware, unauthedMiddleware } from "@/routes/-middlewares";

const shareSettingsSchema = z.object({
	visibility: z.enum(["private", "public", "organization", "team"]).optional(),
	organizationId: z.string().nullable().optional(),
	teamId: z.string().nullable().optional(),
	shareExpiresAt: z.coerce.date().nullable().optional(),
	maxCompletionsPerUser: z.number().int().positive().nullable().optional(),
});

export const $getContentVersion = createServerFn({
	method: "GET",
})
	.middleware([authMiddleware])
	.inputValidator(z.object({ versionId: z.string() }))
	.handler(async ({ context, data }) => {
		return context.controllers.contentController.getVersion(data.versionId);
	});

export const $getLatestContentVersion = createServerFn({ method: "GET" })
	.middleware([authMiddleware])
	.inputValidator(z.object({ threadId: z.string() }))
	.handler(async ({ context, data }) => {
		const latestVersion = await context.controllers.contentController.getLatestVersion(
			data.threadId,
		);

		if (!latestVersion) {
			return {
				data: null,
			};
		}

		return {
			data: latestVersion,
		};
	});

/**
 * Update thread output.
 * Creates a new version snapshot and also updates thread.output for backward compat.
 */
export const $updateThreadOutput = createServerFn({
	method: "POST",
})
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			threadId: z.string(),
			output: z.looseRecord(z.string(), z.any()),
			shareSettings: shareSettingsSchema.optional(),
		}),
	)
	.handler(async ({ context, data }) => {
		return context.controllers.contentController.createVersion(
			data.threadId,
			data.output as Record<string, any>,
			undefined,
			data.shareSettings,
		);
	});

/**
 * Get version access state by ID (no auth required)
 */
export const $getContentVersionAccess = createServerFn({
	method: "GET",
})
	.middleware([unauthedMiddleware])
	.inputValidator(z.object({ versionId: z.string() }))
	.handler(async ({ context, data }) => {
		return context.controllers.contentController.getVersionAccess(data.versionId);
	});

/**
 * List all versions for a thread (auth required, owner only)
 */
export const $listContentVersions = createServerFn({
	method: "GET",
})
	.middleware([authMiddleware])
	.inputValidator(z.object({ threadId: z.string() }))
	.handler(async ({ context, data }) => {
		return context.controllers.contentController.listVersions(data.threadId);
	});

/**
 * List the current user's own versions (auth required)
 */
export const $listUserContentVersions = createServerFn({
	method: "GET",
})
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			page: z.number().optional(),
			pageSize: z.number().optional(),
		}),
	)
	.handler(async ({ context, data }) => {
		return context.controllers.contentController.listUserVersions(data);
	});

/**
 * List public versions (no auth required)
 */
export const $listPublicVersions = createServerFn({
	method: "GET",
})
	.middleware([unauthedMiddleware])
	.inputValidator(
		z.object({
			page: z.number().optional(),
			pageSize: z.number().optional(),
		}),
	)
	.handler(async ({ context, data }) => {
		return context.controllers.contentController.listPublicVersions(data);
	});

/**
 * Update version visibility (auth required, owner only)
 */
export const $updateVersionVisibility = createServerFn({
	method: "POST",
})
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			versionId: z.string(),
			shareSettings: shareSettingsSchema,
		}),
	)
	.handler(async ({ context, data }) => {
		return context.controllers.contentController.updateVersionVisibility(
			data.versionId,
			data.shareSettings,
		);
	});

export const $joinSharedVersion = createServerFn({
	method: "POST",
})
	.middleware([authMiddleware])
	.inputValidator(z.object({ versionId: z.string() }))
	.handler(async ({ context, data }) => {
		await context.controllers.contentController.joinSharedVersion(data.versionId);
		return context.controllers.contentController.getVersionAccess(data.versionId);
	});

/**
 * List attempts for a version.
 * Creator sees all users' attempts; non-creators see only their own.
 */
export const $listVersionAttempts = createServerFn({
	method: "GET",
})
	.middleware([unauthedMiddleware])
	.inputValidator(z.object({ versionId: z.string() }))
	.handler(async ({ context, data }) => {
		return context.controllers.contentController.listVersionAttempts(data.versionId);
	});

export const $getOrCreateVersionAttempt = createServerFn({
	method: "GET",
})
	.middleware([authMiddleware])
	.inputValidator(z.object({ versionId: z.string() }))
	.handler(async ({ context, data }) => {
		return context.controllers.contentController.getOrCreateVersionAttempt(data.versionId);
	});

export const $saveVersionAttemptProgress = createServerFn({
	method: "POST",
})
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			versionId: z.string(),
			blockId: z.string(),
			attemptId: z.string(),
			engineState: z.record(z.string(), z.any()),
		}),
	)
	.handler(async ({ context, data }) => {
		return context.controllers.contentController.saveVersionAttemptProgress(
			data.versionId,
			data.blockId,
			data.attemptId,
			data.engineState,
		);
	});

export const $recordVersionCompletion = createServerFn({
	method: "POST",
})
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			versionId: z.string(),
			attemptId: z.string(),
			completionData: z.unknown().optional(),
		}),
	)
	.handler(async ({ context, data }) => {
		await context.controllers.contentController.recordVersionCompletion(
			data.versionId,
			data.attemptId,
			data.completionData,
		);
		return { success: true };
	});

/**
 * Delete a version attempt (admin only)
 */
export const $deleteVersionAttempt = createServerFn({
	method: "POST",
})
	.middleware([adminMiddleware])
	.inputValidator(z.object({ attemptId: z.string() }))
	.handler(async ({ context, data }) => {
		await context.controllers.contentController.deleteVersionAttempt(data.attemptId);
		return { success: true };
	});

/**
 * List all mistakes across the current user's completed attempts, grouped by version.
 * Supports server-side keyword search, block type filter, and pagination.
 */
export const $searchUserMistakes = createServerFn({
	method: "GET",
})
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			page: z.number().optional(),
			pageSize: z.number().optional(),
			keyword: z.string().optional(),
			blockType: z.string().optional(),
		}),
	)
	.handler(async ({ context, data }) => {
		return context.controllers.contentController.listUserMistakes(data);
	});

/**
 * Get semantically similar questions for a given content block.
 * Used by the "similar question recommendation" feature on the mistakes page.
 */
export const $getSimilarQuestions = createServerFn({
	method: "GET",
})
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			contentBlockId: z.string().min(1),
			blockType: z.string().optional(),
			limit: z.number().int().min(1).max(10).optional(),
		}),
	)
	.handler(async ({ context, data }) => {
		return context.controllers.contentController.getSimilarQuestions(data);
	});

/**
 * Get aggregated weak knowledge points for the current user.
 * Returns a ranked list of topics where the student struggles most.
 */
export const $getUserWeakTopics = createServerFn({
	method: "GET",
})
	.middleware([authMiddleware])
	.handler(async ({ context }) => {
		return context.controllers.contentController.getUserWeakTopics();
	});
